import {
	Chart,
	BarController,
	BarElement,
	DoughnutController,
	ArcElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
} from 'chart.js'
import Alpine from 'alpinejs'

Chart.register(
	BarController,
	BarElement,
	DoughnutController,
	ArcElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
)

const prefersReducedMotion = window.matchMedia(
	'(prefers-reduced-motion: reduce)',
).matches

const seriesPalette = [
	'--brand-turquoise',
	'--brand-orange',
	'--brand-purple',
	'--brand-chartreuse',
	'--brand-cyan',
	'--brand-lavender',
	'--brand-ink',
]

// Resolve CSS custom properties from the element's own computed styles.
// Because custom properties cascade, reading off the chart's canvas picks
// up any theme--* ancestor's tokens — not the root document.
function readToken(name, el) {
	return getComputedStyle(el ?? document.documentElement)
		.getPropertyValue(name)
		.trim()
}

function colourForIndex(i, el) {
	return readToken(seriesPalette[i % seriesPalette.length], el)
}

function groupBySeries(points) {
	const seriesOrder = []
	const seriesMap = new Map()
	const categoryOrder = []
	const categorySet = new Set()

	for (const p of points) {
		const series = p.series || ''
		if (!seriesMap.has(series)) {
			seriesMap.set(series, new Map())
			seriesOrder.push(series)
		}
		if (!categorySet.has(p.category)) {
			categorySet.add(p.category)
			categoryOrder.push(p.category)
		}
		seriesMap.get(series).set(p.category, Number(p.value))
	}

	return { seriesOrder, seriesMap, categoryOrder }
}

function buildBarConfig(points, units, el) {
	const { seriesOrder, seriesMap, categoryOrder } = groupBySeries(points)

	const datasets = seriesOrder.map((seriesLabel, i) => ({
		label: seriesLabel || 'Total',
		data: categoryOrder.map(
			(cat) => seriesMap.get(seriesLabel).get(cat) ?? null,
		),
		backgroundColor: colourForIndex(i, el),
		borderRadius: 4,
		borderSkipped: false,
	}))

	const hasMultipleSeries = seriesOrder.length > 1 && seriesOrder[0] !== ''
	const scale = scaleDefaults(el)

	return {
		type: 'bar',
		data: { labels: categoryOrder, datasets },
		options: {
			responsive: true,
			maintainAspectRatio: false,
			animation: prefersReducedMotion
				? false
				: { duration: 700, easing: 'easeOutQuart' },
			plugins: {
				legend: {
					display: hasMultipleSeries,
					position: 'top',
					align: 'start',
					labels: {
						color: readToken('--foreground', el),
						font: { family: readToken('--font-body', el), size: 13 },
						usePointStyle: true,
						boxWidth: 8,
						boxHeight: 8,
					},
				},
				tooltip: tooltipDefaults(units, el),
			},
			scales: {
				x: scale,
				y: {
					...scale,
					ticks: { ...scale.ticks, callback: (v) => v + units },
				},
			},
		},
	}
}

function buildDonutConfig(points, units, el) {
	// Donut ignores series — every row becomes a slice.
	const labels = points.map((p) => p.category)
	const data = points.map((p) => Number(p.value))
	const colours = points.map((_, i) => colourForIndex(i, el))

	return {
		type: 'doughnut',
		data: {
			labels,
			datasets: [
				{
					data,
					backgroundColor: colours,
					borderColor: readToken('--background', el),
					borderWidth: 3,
				},
			],
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			cutout: '60%',
			animation: prefersReducedMotion
				? false
				: { duration: 700, easing: 'easeOutQuart' },
			plugins: {
				legend: {
					position: 'right',
					align: 'center',
					labels: {
						color: readToken('--foreground', el),
						font: { family: readToken('--font-body', el), size: 13 },
						usePointStyle: true,
						boxWidth: 10,
						boxHeight: 10,
						padding: 14,
					},
				},
				tooltip: tooltipDefaults(units, el),
			},
		},
	}
}

function scaleDefaults(el) {
	return {
		grid: { color: readToken('--border', el), drawBorder: false },
		ticks: {
			color: readToken('--muted-foreground', el),
			font: { family: readToken('--font-body', el), size: 12 },
		},
		border: { display: false },
	}
}

function tooltipDefaults(units, el) {
	return {
		backgroundColor: readToken('--brand-ink', el),
		titleColor: readToken('--brand-paper', el),
		bodyColor: readToken('--brand-paper', el),
		borderColor: readToken('--brand-turquoise', el),
		borderWidth: 1,
		padding: 12,
		cornerRadius: 8,
		titleFont: {
			family: readToken('--font-body', el),
			weight: '500',
			size: 13,
		},
		bodyFont: { family: readToken('--font-body', el), size: 13 },
		displayColors: false,
		callbacks: {
			label: (ctx) => {
				const seriesPrefix =
					ctx.dataset.label && ctx.dataset.label !== 'Total'
						? `${ctx.dataset.label}: `
						: ''
				return `${seriesPrefix}${ctx.parsed.y ?? ctx.parsed}${units}`
			},
		},
	}
}

Alpine.data('chart', (raw) => ({
	config: raw,
	chart: null,
	started: false,
	start() {
		if (this.started) return
		this.started = true
		const canvas = this.$refs.canvas
		if (!canvas) return
		const builder =
			this.config.type === 'donut' ? buildDonutConfig : buildBarConfig
		const cfg = builder(this.config.points, this.config.units || '', canvas)
		this.chart = new Chart(canvas, cfg)
	},
	destroy() {
		if (this.chart) this.chart.destroy()
	},
}))
